import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useValidationFallback } from "@/components/match/use-match-editor";
import { Button } from "@/components/design/Button";
import { FileDrop } from "@/components/share/FileDrop";
import { ImageGallery } from "@/components/share/ImageGallery";
import { normalizeMain, storedImages, type UploadedFile } from "@/components/share/uploaded-file";
import {
  ListingEditDataDocument,
  SaveListingDocument,
  type UpdateListingDataInput,
} from "@/graphql/generated/operations";
import { formatMegabytes, MAX_LISTING_IMAGE_BYTES, RASTER_IMAGE_ACCEPT } from "@/lib/upload-limits";
import { userErrorMessage } from "@/lib/user-error";
import { throwIfRejected, ValidationRejected } from "./listing-form";
import { countImages, gallerySources, listingImageErrorMessage, listingImageWrites } from "./listing-images";
import { forgetListingImages, useListing } from "./use-listing";

// Die Fläche "Bilder" einer Ausschreibung: Titelbild und weitere Bilder, wie beim Beitrag als eine
// Liste, in der genau eines die Vorschau ist (listing-images.ts). Wer nur lesen darf, sieht sie als
// Galerie.
//
// Gespeichert wird mit einem Knopf, nicht im Autosave des Dashboards: ein Bild ist ein Upload von
// mehreren Megabyte, und was genesis davon hält, kennt die Oberfläche erst nach dem erneuten Lesen
// (neue Ids, skalierte Fassungen). Passen nicht alle Bilder in eine Anfrage, folgen sie in eigenen; fehlt
// danach eins, sagt die Fläche das hier, statt die Seite zu wechseln.
export function ListingImagesFacet({ listingId, readOnly, onSaved }: {
  listingId: string;
  readOnly: boolean;
  /** Bilder sind gespeichert: die Fläche gehört ab jetzt zur Ausschreibung. */
  onSaved: () => void;
}) {
  return readOnly ? <ListingGallery listingId={listingId} /> : <ListingImagesEditor listingId={listingId} onSaved={onSaved} />;
}

function ListingGallery({ listingId }: { listingId: string }) {
  const { listing } = useListing(listingId);
  const images = listing ? gallerySources(listing) : [];
  if (images.length === 0) {
    return (
      <p className="smycles-field-meta text-ink-muted">
        <FormattedMessage id="share.listings.images.none" defaultMessage="Noch keine Bilder." />
      </p>
    );
  }
  return (
    <div className="max-w-3xl">
      <ImageGallery images={images} alt={listing?.name ?? ""} heroId="listing-hero" />
    </div>
  );
}

function ListingImagesEditor({ listingId, onSaved }: { listingId: string; onSaved: () => void }) {
  const intl = useIntl();
  // Die Originale, nicht die skalierte Vorschau: wird ein anderes Bild zur Vorschau, geht das bisherige
  // Titelbild als weiteres Bild neu hinaus, und dafür braucht es seine Bytes.
  const query = useQuery(ListingEditDataDocument, { variables: { id: listingId }, fetchPolicy: "network-only" });
  const [notice, setNotice] = useState<string | null>(null);
  // Zählt die gelesenen Stände: ein neuer Stand setzt die Liste neu auf.
  const [revision, setRevision] = useState(0);
  const listing = query.data?.listing ?? null;

  if (!listing) {
    return (
      <p className="smycles-field-meta text-ink-muted" aria-busy={query.loading || undefined}>
        {query.error ? (
          userErrorMessage(intl, query.error)
        ) : (
          <FormattedMessage id="common.loading" defaultMessage="Laden…" />
        )}
      </p>
    );
  }
  return (
    <ImagesForm
      key={revision}
      listingId={listingId}
      initial={storedImages(listing, intl)}
      notice={notice}
      onSaved={async (missing) => {
        onSaved();
        setNotice(missing);
        // Was genesis jetzt hält, mit seinen Ids: ein zweites Speichern lädt nichts doppelt hoch. Scheitert
        // das Lesen, steht die Ladefehler-Meldung statt einer Liste, die nicht mehr stimmt.
        await query.refetch().catch(() => undefined);
        setRevision((current) => current + 1);
      }}
    />
  );
}

function ImagesForm({
  listingId,
  initial,
  notice,
  onSaved,
}: {
  listingId: string;
  initial: UploadedFile[];
  notice: string | null;
  /** `missing`: die Meldung zu Bildern, die nicht mehr ankamen, sonst null. */
  onSaved: (missing: string | null) => Promise<void>;
}) {
  const intl = useIntl();
  const client = useApolloClient();
  const fallback = useValidationFallback();
  const [saveListing] = useMutation(SaveListingDocument);
  const [images, setImages] = useState<UploadedFile[]>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const writes = listingImageWrites(initial, images);
  const dirty = Object.keys(writes.first).length > 0 || writes.followUps.length > 0;

  async function write(data: UpdateListingDataInput) {
    const result = await saveListing({ variables: { id: listingId, input: { data, dryRun: false } } });
    throwIfRejected(result.data?.updateListing, fallback);
  }

  async function save() {
    if (saving || !dirty) return;
    setSaving(true);
    setError(null);
    try {
      if (Object.keys(writes.first).length > 0) await write(writes.first);
    } catch (e) {
      // Nichts ist angekommen: die Liste bleibt, wie sie ist, und ein zweites Speichern versucht es neu.
      setError(
        e instanceof ValidationRejected ? e.message : (listingImageErrorMessage(intl, e) ?? userErrorMessage(intl, e)),
      );
      setSaving(false);
      return;
    }
    // Was nicht mehr in die erste Anfrage passte, in eigenen Paketen. Scheitert eins, steht der Rest
    // trotzdem; die Fläche sagt, wie viele fehlen, und zeigt den Stand, den genesis jetzt hält.
    let missing: string | null = null;
    for (let index = 0; index < writes.followUps.length; index++) {
      try {
        await write({ images: writes.followUps[index] });
      } catch (e) {
        missing = intl.formatMessage(
          {
            id: "share.listings.images.notSaved",
            defaultMessage:
              "Die Ausschreibung ist gespeichert, {count, plural, one {ein Bild fehlt} other {# Bilder fehlen}} aber noch. {reason}",
          },
          {
            count: countImages(writes.followUps.slice(index)),
            reason: listingImageErrorMessage(intl, e) ?? userErrorMessage(intl, e),
          },
        );
        break;
      }
    }
    // Keine Antwort trägt die Bilder; Dashboard, Galerie und Kacheln lesen sie beim nächsten Mal neu.
    forgetListingImages(client.cache, listingId);
    await onSaved(missing);
  }

  return (
    <div className="space-y-4">
      <FileDrop
        headerAction={
          <Button id="listing-images-save" size="compact" disabled={saving || !dirty} onClick={() => void save()}>
            {saving ? <FormattedMessage id="common.saving" defaultMessage="Speichern…" />
              : <FormattedMessage id="common.save" defaultMessage="Speichern" />}
          </Button>
        }
        idPrefix="listing-images"
        label={intl.formatMessage({ id: "share.listings.form.images", defaultMessage: "Bilder" })}
        accept={RASTER_IMAGE_ACCEPT}
        maxBytes={MAX_LISTING_IMAGE_BYTES}
        hint={
          <FormattedMessage
            id="share.listings.form.imagesHint"
            defaultMessage="PNG, JPEG, GIF, BMP, TIFF oder WebP, je Bild höchstens {max}"
            values={{ max: formatMegabytes(MAX_LISTING_IMAGE_BYTES) }}
          />
        }
        items={images}
        onChange={(next) => setImages(normalizeMain(next))}
        onAppend={(next) => setImages((current) => normalizeMain([...current, ...next]))}
        onSelectMain={(index) =>
          setImages((current) => current.map((file, i) => ({ ...file, isMain: i === index })))
        }
      />
      {error || notice ? (
        <p role="alert" className="text-smycles-meta text-ink-accent">
          {error ?? notice}
        </p>
      ) : null}

    </div>
  );
}
